"use client";

export interface ViewSwitchOption<T extends string> {
  id: T;
  label: string;
}

interface ViewSwitchProps<T extends string> {
  /** The group's accessible name ("Order", "Sort"). */
  label: string;
  options: readonly ViewSwitchOption<T>[];
  value: T;
  onChange: (id: T) => void;
  className?: string;
}

/**
 * Up to four views of one list as underline tabs (the cohesion plan's one
 * view control; past four, use a select). The selected view is ink plus a
 * 2px stroke, never a filled pill. Each tab is a toggle button so the state
 * reads as pressed or not.
 */
export function ViewSwitch<T extends string>({ label, options, value, onChange, className }: ViewSwitchProps<T>) {
  return (
    <div role="group" aria-label={label} className={`flex flex-wrap items-center gap-x-4 ${className ?? ""}`}>
      {options.map(({ id, label: optionLabel }) => (
        <button
          key={id}
          type="button"
          aria-pressed={value === id}
          onClick={() => onChange(id)}
          className="tlsr-focus tls-target cursor-pointer border-0 border-b-2 border-solid bg-transparent px-0 text-sm font-semibold"
          style={{
            borderBottomColor: value === id ? "var(--tls-brand)" : "transparent",
            color: value === id ? "var(--tls-fg-strong)" : "var(--tls-fg-muted)",
          }}
        >
          {optionLabel}
        </button>
      ))}
    </div>
  );
}
